/**
 * Utilidades para manejo de fechas - Solución para problemas de zona horaria
 */

/**
 * Interpreta un datetime del backend como UTC real.
 * El backend guarda timestamps naive (datetime.utcnow(), sin 'Z' ni offset).
 * Si se le pasa un string así a `new Date(...)` directamente, el motor JS lo
 * interpreta como hora LOCAL del navegador en vez de UTC, mostrando una hora
 * adelantada (o el día equivocado) en Chile. Por eso agregamos 'Z' cuando el
 * string no trae ya información de zona horaria.
 */
const parseUtcNaive = (dateString: string): Date => {
  const tieneZonaHoraria = /Z$|[+-]\d{2}:\d{2}$/.test(dateString);
  return new Date(tieneZonaHoraria ? dateString : `${dateString}Z`);
};

/**
 * Formatea una fecha de forma segura evitando problemas de zona horaria
 * Para fechas que vienen como "YYYY-MM-DD" del backend
 */
export const formatDateSafe = (dateString: string): string => {
  if (!dateString) return '';

  // Si viene solo fecha (YYYY-MM-DD), crear Date sin problemas de timezone
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month es 0-indexed

    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Si viene con hora (datetime naive UTC), interpretarla como UTC real
  const date = parseUtcNaive(dateString);

  // Verificar si la fecha es válida
  if (isNaN(date.getTime())) {
    return dateString; // Devolver original si no se puede parsear
  }

  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formatea fecha y hora de forma completa, corrigiendo el desfase de
 * zona horaria (ver parseUtcNaive)
 */
export const formatDateTimeSafe = (dateString: string): string => {
  if (!dateString) return '';

  const date = parseUtcNaive(dateString);

  if (isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Convierte fecha a formato ISO sin problemas de zona horaria
 * Para envío al backend
 */
export const toISODateSafe = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Crea una fecha desde string YYYY-MM-DD sin problemas de timezone
 */
export const createDateFromISOString = (dateString: string): Date => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    throw new Error('Formato de fecha inválido. Use YYYY-MM-DD');
  }
  
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Obtiene fecha actual en formato YYYY-MM-DD
 */
export const getCurrentDateISO = (): string => {
  return toISODateSafe(new Date());
};

/**
 * Compara dos fechas (solo fecha, ignora hora)
 */
export const compareDatesOnly = (date1: string, date2: string): number => {
  const d1 = createDateFromISOString(date1);
  const d2 = createDateFromISOString(date2);
  
  return d1.getTime() - d2.getTime();
};
